import React, { useEffect, useState } from "react";
import DashboardHeader from "../../components/auth/DashboardHeader";
import Sidebar from "../../components/auth/Sidebar";
import { Loader } from "../../components/Loader";
import API from "../../helper/AxiosInterceptor";
import { toast } from "react-toastify";
import copy from "copy-to-clipboard";
import { FaCopy } from "react-icons/fa";
import { useAppSelection } from "../../context/AppSelectionContext";

const WithdrawalSettings = () => {
    const { selectedApp, setSelectedApp, apps, fetchApps } = useAppSelection();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [settings, setSettings] = useState({
        isUserWithdrawalEnabled: false,
        isAutoWithdrawalEnabled: false,
        maxAutoWithdrawalLimit: 0,
        minWithdrawalAmount: 0,
        dailyWithdrawalRequestLimit: 0,
        dailyWithdrawalAmountLimit: 0,
        withdrawalCooldownMinutes: 0,
    });
    const [walletBalances, setWalletBalances] = useState([]);
    const [isLoadingBalances, setIsLoadingBalances] = useState(false);

    // Currency and Price State
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const [cryptoPrices, setCryptoPrices] = useState({});

    // Fetch crypto prices via backend proxy (Tatum)
    const getCryptoPrice = async () => {
        try {
            const currency = selectedCurrency.toLowerCase();
            const symbols = "BTC,ETH,BNB,USDT,USDC,MATIC,AVAX,TRX";

            const response = await API.post(`${process.env.REACT_APP_API_URL}/common/price`, {
                currency: currency.toUpperCase(),
                symbol: symbols
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response?.data?.success && response?.data?.data) {
                const prices = response.data.data;
                setCryptoPrices({
                    BTC: prices.BTC,
                    ETH: prices.ETH,
                    BNB: prices.BNB,
                    WBNB: prices.BNB,
                    USDT: prices.USDT,
                    USDC: prices.USDC,
                    MATIC: prices.MATIC,
                    AVAX: prices.AVAX,
                    TRX: prices.TRX,
                });
            }
        } catch (error) {
            console.error("Error fetching crypto prices:", error);
        }
    };

    useEffect(() => {
        getCryptoPrice();
        const interval = setInterval(getCryptoPrice, 60000);
        return () => clearInterval(interval);
    }, [selectedCurrency]);

    // Load settings from selected app
    const loadAppSettings = (app) => {
        setSettings({
            isUserWithdrawalEnabled: app.isUserWithdrawalEnabled || false,
            isAutoWithdrawalEnabled: app.isAutoWithdrawalEnabled || false,
            maxAutoWithdrawalLimit: app.maxAutoWithdrawalLimit || 0,
            minWithdrawalAmount: app.minWithdrawalAmount || 0,
            dailyWithdrawalRequestLimit: app.dailyWithdrawalRequestLimit || 0,
            dailyWithdrawalAmountLimit: app.dailyWithdrawalAmountLimit || 0,
            withdrawalCooldownMinutes: app.withdrawalCooldownMinutes || 0,
        });
    };

    // Fetch wallet balances for an app
    const fetchWalletBalances = async (appId) => {
        try {
            setIsLoadingBalances(true);
            const resp = await API.get(
                `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/balance`,
                {
                    params: { appId },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            setWalletBalances(resp?.data?.data?.balances || []);
        } catch (error) {
            console.error("Error fetching wallet balances:", error);
            setWalletBalances([]);
        } finally {
            setIsLoadingBalances(false);
        }
    };

    useEffect(() => {
        fetchApps();
    }, []);

    // Load settings + balances when selectedApp changes
    useEffect(() => {
        if (selectedApp && apps.length > 0) {
            const app = apps.find((a) => a._id === selectedApp);
            if (app) {
                loadAppSettings(app);
            }
            fetchWalletBalances(selectedApp);
        }
    }, [selectedApp, apps]);

    const handleAppChange = (e) => {
        const appId = e.target.value;
        setSelectedApp(appId);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : parseFloat(value) || 0,
        }));
    };

    const handleSave = async () => {
        if (!selectedApp) {
            toast.error("Please select an app");
            return;
        }

        try {
            setIsSaving(true);
            await API.put(
                `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/settings`,
                {
                    appId: selectedApp,
                    ...settings,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            toast.success("Settings saved successfully!");
        } catch (error) {
            console.error("Error saving settings:", error);
            toast.error(error?.response?.data?.message || "Failed to save settings");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <DashboardHeader />

            <div className="site-bg">
                <div className="dashborad-content-bg">
                    <aside className="sidebar">
                        <Sidebar />
                    </aside>
                    <div className="dashborad-warper" style={{ position: "relative" }}>
                        {isLoading && <Loader inline={true} />}
                        <div className="transactions-list">
                            <div className="dashborad-head">
                                <div className="breadcrumb-menu">
                                    <h3>Withdrawal Settings</h3>
                                    <p>Configure withdrawal limits and auto-approval thresholds</p>
                                </div>
                            </div>

                            <div className="white-bg my-apps-content padd20">
                                {/* App Selector */}
                                <div className="form-group" style={{ marginBottom: "30px" }}>
                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                        Select Application
                                    </label>
                                    <select
                                        className="form-control"
                                        value={selectedApp}
                                        onChange={handleAppChange}
                                        style={{ maxWidth: "400px" }}
                                    >
                                        <option value="">Select App</option>
                                        {apps.map((app) => (
                                            <option key={app._id} value={app._id}>
                                                {app.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedApp && (
                                    <>
                                        {/* Wallet Balances Section */}
                                        <div style={{ marginBottom: "30px" }}>
                                            <h4 style={{ marginBottom: "15px", color: "#333", display: "flex", alignItems: "center" }}>
                                                Wallet Balances
                                                {isLoadingBalances && (
                                                    <span style={{
                                                        marginLeft: "15px",
                                                        display: "inline-block",
                                                        width: "20px",
                                                        height: "20px",
                                                        border: "2px solid #f3f3f3",
                                                        borderTop: "2px solid #F3BA2F",
                                                        borderRadius: "50%",
                                                        animation: "spin 1s linear infinite"
                                                    }} />
                                                )}
                                            </h4>
                                            <style>
                                                {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                                            </style>
                                            <p style={{ color: "#666", fontSize: "13px", marginBottom: "20px" }}>
                                                Monitor your wallet balances across different chains. Withdrawals on chains with low funds will require manual approval.
                                            </p>

                                            {isLoadingBalances ? (
                                                <div style={{
                                                    textAlign: "center",
                                                    padding: "40px",
                                                    backgroundColor: "#f9f9f9",
                                                    borderRadius: "8px",
                                                    border: "1px solid #eee"
                                                }}>
                                                    <div style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        margin: "0 auto 15px",
                                                        border: "3px solid #f3f3f3",
                                                        borderTop: "3px solid #F3BA2F",
                                                        borderRadius: "50%",
                                                        animation: "spin 1s linear infinite"
                                                    }} />
                                                    <div style={{ color: "#666" }}>Fetching balances from all chains...</div>
                                                </div>
                                            ) : walletBalances.length > 0 ? (
                                                <div className="table-box" style={{ overflowX: "auto" }}>
                                                    <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Coin Name</th>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Blockchain Network</th>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Code</th>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Wallet Address</th>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Balance</th>
                                                                <th style={{ padding: "12px 15px", textAlign: "left", fontWeight: "600", color: "#333" }}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                                        <span>Value</span>
                                                                        <select
                                                                            value={selectedCurrency}
                                                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                                                            style={{
                                                                                padding: "4px 8px",
                                                                                borderRadius: "4px",
                                                                                border: "1px solid #ddd",
                                                                                backgroundColor: "#fff",
                                                                                cursor: "pointer"
                                                                            }}
                                                                        >
                                                                            <option value="USD">USD</option>
                                                                            <option value="TRY">TRY</option>
                                                                            <option value="AUD">AUD</option>
                                                                        </select>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {walletBalances.map((bal, idx) => {
                                                                const balance = parseFloat(bal.balance) || 0;

                                                                // Map symbol/code to cryptoPrices key
                                                                // Use logic from AppsDetail.js: split code by '.'
                                                                let priceKey = (bal.code?.split(".")[0] ?? bal.symbol)?.toUpperCase();

                                                                if (priceKey === "WBNB") priceKey = "BNB";
                                                                if (priceKey === "POLYGON") priceKey = "MATIC";

                                                                const tokenPrice = cryptoPrices[priceKey] || 0;
                                                                const value = balance * tokenPrice;
                                                                const currencySymbol = selectedCurrency === "USD" ? "$" : selectedCurrency === "TRY" ? "₺" : "AU$";

                                                                return (<tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                                                    <td style={{ padding: "12px 15px", color: "#333", fontWeight: "500" }}>
                                                                        {bal.symbol}
                                                                    </td>
                                                                    <td style={{ padding: "12px 15px", color: "#666" }}>
                                                                        {bal.network || bal.chainId}
                                                                    </td>
                                                                    <td style={{ padding: "12px 15px", color: "#666" }}>
                                                                        {bal.symbol}
                                                                    </td>
                                                                    <td style={{ padding: "12px 15px", color: "#666" }}>
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                            <span title={bal.walletAddress}>
                                                                                {bal.walletAddress ? `${bal.walletAddress.substring(0, 6)}...${bal.walletAddress.substring(bal.walletAddress.length - 4)}` : "N/A"}
                                                                            </span>
                                                                            {bal.walletAddress && (
                                                                                <FaCopy
                                                                                    style={{ cursor: "pointer", color: "#F3BA2F" }}
                                                                                    onClick={() => {
                                                                                        copy(bal.walletAddress);
                                                                                        toast.success("Address Copied!");
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{
                                                                        padding: "12px 15px",
                                                                        color: balance > 0 ? "#28a745" : "#666",
                                                                        fontWeight: "500"
                                                                    }}>
                                                                        {bal.balance === "Error" ? "⚠ Error" : balance.toFixed(6)}
                                                                    </td>
                                                                    <td style={{ padding: "12px 15px", color: "#333" }}>
                                                                        {currencySymbol} {value.toFixed(5)}
                                                                    </td>
                                                                </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div style={{
                                                    color: "#666",
                                                    padding: "30px",
                                                    textAlign: "center",
                                                    backgroundColor: "#f9f9f9",
                                                    borderRadius: "8px",
                                                    border: "1px solid #eee"
                                                }}>
                                                    No wallet balances available
                                                </div>
                                            )}
                                        </div>

                                        {/* Enable/Disable Toggles */}
                                        <div style={{ marginBottom: "30px" }}>
                                            <h4 style={{ marginBottom: "15px", color: "#333" }}>General Settings</h4>

                                            <div className="form-check" style={{ marginBottom: "15px" }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="isUserWithdrawalEnabled"
                                                    name="isUserWithdrawalEnabled"
                                                    checked={settings.isUserWithdrawalEnabled}
                                                    onChange={handleInputChange}
                                                    style={{ marginRight: "10px" }}
                                                />
                                                <label className="form-check-label" htmlFor="isUserWithdrawalEnabled">
                                                    <strong>Enable User Withdrawals</strong>
                                                    <p style={{ color: "#666", fontSize: "13px", margin: "4px 0 0" }}>
                                                        Allow users to request withdrawals through your platform
                                                    </p>
                                                </label>
                                            </div>

                                            <div className="form-check" style={{ marginBottom: "15px" }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="isAutoWithdrawalEnabled"
                                                    name="isAutoWithdrawalEnabled"
                                                    checked={settings.isAutoWithdrawalEnabled}
                                                    onChange={handleInputChange}
                                                    style={{ marginRight: "10px" }}
                                                />
                                                <label className="form-check-label" htmlFor="isAutoWithdrawalEnabled">
                                                    <strong>Enable Auto-Approval</strong>
                                                    <p style={{ color: "#666", fontSize: "13px", margin: "4px 0 0" }}>
                                                        Automatically approve withdrawals below the specified limit
                                                    </p>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Limits */}
                                        <div style={{ marginBottom: "30px" }}>
                                            <h4 style={{ marginBottom: "15px", color: "#333" }}>Limits</h4>

                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                                                <div className="form-group">
                                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                        Max Auto-Approval Limit (USD)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="maxAutoWithdrawalLimit"
                                                        value={settings.maxAutoWithdrawalLimit}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <small style={{ color: "#666" }}>
                                                        Withdrawals above this amount require manual approval
                                                    </small>
                                                </div>

                                                <div className="form-group">
                                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                        Minimum Withdrawal (USD)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="minWithdrawalAmount"
                                                        value={settings.minWithdrawalAmount}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <small style={{ color: "#666" }}>
                                                        Users cannot withdraw less than this amount
                                                    </small>
                                                </div>

                                                <div className="form-group">
                                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                        Daily Request Limit (per user)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="dailyWithdrawalRequestLimit"
                                                        value={settings.dailyWithdrawalRequestLimit}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <small style={{ color: "#666" }}>
                                                        0 = unlimited requests per day
                                                    </small>
                                                </div>

                                                <div className="form-group">
                                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                        Daily Amount Limit (USD per user)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="dailyWithdrawalAmountLimit"
                                                        value={settings.dailyWithdrawalAmountLimit}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <small style={{ color: "#666" }}>
                                                        0 = unlimited daily withdrawal amount
                                                    </small>
                                                </div>

                                                <div className="form-group">
                                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                        Cooldown Period (minutes)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="withdrawalCooldownMinutes"
                                                        value={settings.withdrawalCooldownMinutes}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <small style={{ color: "#666" }}>
                                                        Time between withdrawal requests. 0 = no cooldown
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Save Button */}
                                        <div style={{ marginTop: "20px" }}>
                                            <button
                                                type="button"
                                                onClick={handleSave}
                                                disabled={isSaving}
                                                style={{
                                                    backgroundColor: "#f5a623",
                                                    color: "#fff",
                                                    border: "none",
                                                    padding: "12px 30px",
                                                    borderRadius: "8px",
                                                    fontSize: "16px",
                                                    fontWeight: "600",
                                                    cursor: isSaving ? "not-allowed" : "pointer",
                                                    opacity: isSaving ? 0.7 : 1,
                                                }}
                                            >
                                                {isSaving ? "Saving..." : "Save Settings"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WithdrawalSettings;
